import { StateControlAbstraction } from "../../state-control.abstraction";
import { toObservable } from "@angular/core/rxjs-interop";
import { signal, Signal, WritableSignal } from "@angular/core";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";

/**
 * Shared contract for period-like controllers (transaction-derived periods, scenario periods,
 * etc). Subclasses own their `_selected` signal and define what "default" means for their
 * domain via getDefaultSelection(), so consumers like ViewsDatepickerDirective can reset to a
 * consistent default without knowing which concrete controller they're talking to.
 */
export abstract class PeriodSelectionController extends StateControlAbstraction {
  protected abstract _selected: WritableSignal<PeriodType>;
  abstract selected: Signal<PeriodType>;

  /**
   * The default SELECTED range. Subclasses override this to define default-selection semantics,
   * e.g. a 12-month cap, so that resetting stays consistent with the default applied on initial
   * load.
   */
  abstract getDefaultSelection(): PeriodType;
}

// CollectionControl
export class PeriodController extends PeriodSelectionController {
  /** Recomputed on every access so long-lived sessions don't seed resets/clears with a stale day. */
  protected get today(): Date {
    return BalanceDatesUtils.getEndOfDayDate(new Date());
  }

  /** Snapshot of `today` for the two field initializers below, so they agree even if
   * construction straddles a day boundary. */
  private readonly constructionDay = this.today;

  /** Public signal for component to listen too **/
  private _options = signal<PeriodType>(this.getInitialValue(this.constructionDay));

  /**
   * This contain the startDate to EndDate = today ) in the system.
   * **/
  options = this._options.asReadonly();

  protected _selected = signal<PeriodType>(this.getInitialValue(this.constructionDay));
  selected = this._selected.asReadonly();

  /** **<span style="color: red;">This is for store use only</span>** **/
  readonly selected$ = toObservable(this.selected);

  /**
   * This contain the startDate to EndDate = today ) in the system.
   * **<span style="color: red;">This is for store use only</span>**
   * **/
  readonly options$ = toObservable(this.options);

  clear() {
    const today = this.today;
    this._options.set(this.getInitialValue(today));
    this._selected.set(this.getInitialValue(today));
  }

  getInitialValue(today: Date = this.today): PeriodType {
    return {
      startDate: today,
      endDate: today,
      _isDefault: true,
    };
  }

  /**
   * Update range selection, normalised to start/end of day. `_isDefault` is taken from `selection`
   * as-is — callers recording a manual pick must pass `_isDefault: false` themselves.
   */
  updateSelection(selection: PeriodType) {
    const adjustedTime = {
      startDate: BalanceDatesUtils.getStartOfDayDate(selection.startDate),
      endDate: BalanceDatesUtils.getEndOfDayDate(selection.endDate),
      _isDefault: selection._isDefault,
    };

    this._selected.set(adjustedTime);
  }

  getDefaultSelection(): PeriodType {
    return { ...this.options(), _isDefault: true };
  }

  /**
   * Update value of the Universe of transaction Range options.
   */
  updateOption(option: PeriodType) {
    const adjustedTime = {
      startDate: BalanceDatesUtils.getStartOfDayDate(option.startDate),
      endDate: BalanceDatesUtils.getEndOfDayDate(option.endDate),
      _isDefault: option._isDefault,
    };
    this._options.set(adjustedTime);
  }

  /**
   * Set the new date range options, and also set the selected range if the user never touched it.
   */
  updateOptionAndSelectionFromTransactions(transactions: readonly TransactionView[]) {
    const { option, defaultSelection } = this.deriveOptionAndDefaultSelection(transactions);

    this.updateOption(option);
    if (this.selected()._isDefault) {
      this.updateSelection(defaultSelection);
    }
  }

  /**
   * Pure preview of the period that would become the default SELECTED range for this
   * transaction set, without writing any signal. Subclasses override this (not the public
   * method above) to change default-selection semantics, e.g. a 12-month cap.
   */
  protected deriveOptionAndDefaultSelection(
    transactions: readonly TransactionView[],
    today: Date = this.today,
  ): {
    option: PeriodType;
    defaultSelection: PeriodType;
  } {
    const period = this.getInitialValue(today);

    for (const transaction of transactions) {
      if (transaction.postDate.getTime() <= period.startDate.getTime()) {
        period.startDate = transaction.postDate;
      }

      if (transaction.postDate.getTime() >= period.endDate.getTime()) {
        period.endDate = transaction.postDate;
      }
    }

    return { option: { ...period }, defaultSelection: { ...period } };
  }

  /**
   * Widens the selected date range just enough to include `date` if it currently falls outside
   * it — used when a global search result is hidden by the active Past View date filter (#5058:
   * clicking a search result must always be able to open it, auto-expanding the date range as
   * needed rather than failing silently). No-op if `date` is already in range.
   *
   * Forces `_isDefault: false` on an actual widen: `updateOptionAndSelectionFromTransactions()`
   * unconditionally overwrites the selection with the freshly-derived default range whenever
   * `_isDefault` is true, and that method runs again right after this call (CalculationBaseStoreService
   * reacts to both the filter and period selection changes the #5058 flow makes). Leaving
   * `_isDefault` true would let that very next cycle immediately re-clamp the range back to the
   * default (e.g. the 12-month cap) before the user ever sees the widened one.
   */
  ensureDateVisible(date: Date): void {
    const current = this.selected();
    if (!current || !date) {
      return;
    }
    const time = date.getTime();
    // An invalid Date (e.g. new Date(NaN)) makes every comparison below false, which would
    // otherwise fall through to updateSelection() with the range unchanged — silently flipping
    // _isDefault to false (disabling future auto-widening) for no actual visibility gain.
    if (Number.isNaN(time)) {
      return;
    }
    const startTime = current.startDate.getTime();
    const endTime = current.endDate.getTime();
    if (time >= startTime && time <= endTime) {
      return;
    }

    this.updateSelection({
      startDate: time < startTime ? date : current.startDate,
      endDate: time > endTime ? date : current.endDate,
      _isDefault: false,
    });
  }

  filterOnPeriod(transactions: readonly TransactionView[]) {
    return transactions.filter((transactionView) => {
      return (
        transactionView?.postDate.getTime() >= this.selected().startDate.getTime() &&
        transactionView?.postDate.getTime() <= this.selected().endDate.getTime()
      );
    });
  }
}
